import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  fetchRooms,
  createRoom,
  updateRoom as apiUpdateRoom,
  updateRoomOrderAPI,
  setRoomUnitySyncAPI,
} from "@/api/roomApi";
import { fetchWithAuth } from "@/api/fetchApi";
import { normalizeRoomSettings } from "@/config/roomSettingsConfig";

export const useRoomStore = defineStore("roomStore", () => {
  const rooms = ref([]); // 初期値を `[]` にすることで Vue 側でエラーが出ないようにする
  const selectedRoomId = ref(null);

  function setSelectedRoom(roomId) {
    selectedRoomId.value = roomId;
  }

  const selectedRoom = computed(() => {
    return rooms.value.find((r) => r.id === selectedRoomId.value) || null;
  });

  async function loadRooms({ filterUnitySync = false } = {}) {
    try {
      const response = await fetchRooms({ filterUnitySync });
      rooms.value = response.map((room) => ({
        ...room,
        // number: Number(room.number),
        // number : room.number,
      }));
      console.log("🔹 API Response:", response);
    } catch (error) {
      console.error("❌ Failed to fetch rooms:", error);
    }
  }

  async function addRoom(name) {
    try {
      const newRoom = await createRoom(name);
      // バックエンドが既存ルームの display_order を +1 してから新ルームを 1 で挿入する。
      // ローカルキャッシュも同じシフトを適用しないと、古い display_order=1 のルームと
      // 衝突して stable sort で既存ルームが先頭に残ってしまう（新ルームが 2 番目に見える）。
      rooms.value = rooms.value.map((r) => ({
        ...r,
        display_order: (r.display_order || 0) + 1,
      }));
      rooms.value.push(newRoom);
      return newRoom;
    } catch (error) {
      console.error("❌ Failed to create room:", error);
    }
  }

  async function updateRoom(updatedRoom) {
    try {
      const result = await apiUpdateRoom(updatedRoom); // API へリクエスト
      const index = rooms.value.findIndex((r) => r.id === updatedRoom.id);
      if (index !== -1) {
        rooms.value.splice(index, 1, result);
      }
    } catch (error) {
      console.error("❌ Error updating room:", error);
    }
  }

  async function deleteRoom(room) {
    try {
      if (!room.id) throw new Error("Room ID is missing");
      await fetchWithAuth(`/rooms/${room.id}/`, { method: "DELETE" });
      rooms.value = rooms.value.filter((f) => f.id !== room.id);
      console.log("✅ Room deleted");
    } catch (error) {
      console.error("❌ Delete failed:", error);
    }
  }

  const getRoomById = (id) => {
    return computed(
      () => rooms.value.find((room) => room.id === Number(id)) || null
    );
  };

  function updateRoomOrder(newLayout) {
    updateRoomOrderAPI(newLayout);
    console.log("✅ Updated room order by roomStore");
  }

  // ✅ 共有ルームの VR デバイス同期 ON/OFF を切り替え（楽観更新 + サーバ反映）
  async function setRoomUnitySync(roomId, enabled) {
    const idx = rooms.value.findIndex((r) => r.id === roomId);
    const prev = idx !== -1 ? rooms.value[idx].unity_sync_enabled : null;
    if (idx !== -1) {
      rooms.value[idx] = { ...rooms.value[idx], unity_sync_enabled: enabled };
    }
    try {
      const res = await setRoomUnitySyncAPI(roomId, enabled);
      if (idx !== -1 && res && typeof res.unity_sync_enabled === "boolean") {
        rooms.value[idx] = { ...rooms.value[idx], unity_sync_enabled: res.unity_sync_enabled };
      }
      return res;
    } catch (e) {
      // 失敗時はロールバック
      if (idx !== -1 && prev !== null) {
        rooms.value[idx] = { ...rooms.value[idx], unity_sync_enabled: prev };
      }
      throw e;
    }
  }

  async function getRoomSettings(roomId) {
    const res = await fetchWithAuth(`/rooms/${roomId}/`);
    if (res) {
      return {
        ...res,
        settings: normalizeRoomSettings(res.settings),
      };
    }
    throw new Error("Failed to fetch room");
  }

  async function updateRoomSettings(room) {
    try {
      const res = await fetchWithAuth(`/rooms/${room.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ settings: room.settings }),
      });

      if (res) {
        const updatedSettings = normalizeRoomSettings(res.settings);

        const index = rooms.value.findIndex((r) => r.id === room.id);
        if (index !== -1) {
          rooms.value[index].settings = updatedSettings;
        }

        return { ...res, settings: updatedSettings };
      }
    } catch (e) {
      console.error("❌ updateRoomSettings 失敗:", e);
      throw e;
    }
  }

  return {
    rooms,
    loadRooms,
    addRoom,
    updateRoom,
    getRoomById,
    updateRoomOrder,
    setRoomUnitySync,
    deleteRoom,
    getRoomSettings,
    updateRoomSettings,

    // RoomDetail 選択管理
    selectedRoomId,
    selectedRoom,
    setSelectedRoom,
  };
});
